import styled from 'styled-components'
import Table from '../table/Table'
import { SkeletonTable } from '../generic/SkeletonLoader'
import { Button } from '../generic/Core'
import useViewTable from './useViewTable'
import ViewTableToolbar from './ViewTableToolbar'
import ListTopBar from './ListTopBar'
import useTranslation from '../../hooks/useTranslation'
import ViewTableRows from './ViewTableRows'
import ViewTableRows_m from './ViewTableRows_m'
import SearchLimitNote from './SearchLimitNote'
import MoveFolderModal from '../documents/MoveFolderModal/MoveFolderModal'

const LoadMore = styled(Button)`
  margin-top: 1rem;
  font-size: var(--font-XS);
`

/**
 * Presentational ViewTable
 * - No data fetching or internal query state.
 * - Controlled by parent (ListView) via props.
 */
const ViewTable = ({
  endpoint,
  table,
  config,
  filter, // active filter index
  id,

  // Controlled state from parent (ListView)
  result, // react-query infinite result from useListData
  order,
  setOrder,
  searchTerm,
  setSearchTerm,
  debouncedSearchTerm,
  filterTerm, // combined filter string (static + user-applied)
  serverFilter, // server-side filter string — export forwards it with the rest
  entity, // effective entity, for resolving export profiles
  userFilterLabel,
  onApplyLocationFilter,
  onClearFilter,
  selectAction,
  onSelectConfirm,
  onRefreshAggregate,
  monthFilter, // { value } when this tab asks for one, otherwise undefined
  onMonthFilterChange,
  topBar, // { cards, extras, tags, createButton } — what ListView adds above the rows
}) => {
  const { t } = useTranslation()
  const currentFilter = config.filters?.list?.[filter]

  const {
    getFilterActions,
    handleAction,
    handleExpand,
    selection,
    onBulkListAction,
    sortList,
    visibleColumns,
    getAllItems,
    moveItem,
    moveLoading,
    confirmMove,
    clearMoveItem,
  } = useViewTable({
    config,
    endpoint,
    table,
    result,
    debouncedSearchTerm,
    filterTerm,
    serverFilter,
    entity,
    order,
    selectAction,
    setOrder,
    onRefreshAggregate,
  })

  const lineActions = getFilterActions('lineActions', currentFilter)
  const listActions = getFilterActions('listActions', currentFilter)

  const handleSelectConfirmClick = () => {
    if (onSelectConfirm) {
      const allItems = getAllItems()
      const selectedItemObjects = allItems.filter(item => selection.selectedItems.has(item.id))
      onSelectConfirm(selectedItemObjects)
    }
  }

  const toolbar = (
    <ListTopBar
      cards={topBar?.cards}
      cardsTop={config.filters?.marginTop}
      filters={
        <ViewTableToolbar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          table={table}
          config={config}
          userFilterLabel={userFilterLabel}
          onApplyLocationFilter={onApplyLocationFilter}
          onClearFilter={onClearFilter}
          monthFilter={monthFilter}
          onMonthFilterChange={onMonthFilterChange}
          extras={topBar?.extras}
        />
      }
      tags={topBar?.tags}
      actions={[
        selectAction?.action === 'nextStep' && selection.selectedCount > 0 && (
          <Button key='next' $variant='primary' $marginRight='0' onClick={handleSelectConfirmClick}>
            {t(selectAction.label)}
          </Button>
        ),
        topBar?.createButton && <span key='new'>{topBar.createButton}</span>,
      ]}
    />
  )

  if (!result || result.isLoading) {
    return (
      <>
        <Table.TitleWrapper name='TableTitleWrapper'>{toolbar}</Table.TitleWrapper>
        <Table.Content><SkeletonTable /></Table.Content>
      </>
    )
  }

  if (result.isError) {
    return (
      <>
        <Table.TitleWrapper name='TableTitleWrapper'>{toolbar}</Table.TitleWrapper>
        <Table.Content><span>Error: {result.error.message}</span></Table.Content>
      </>
    )
  }

  if (result.isSuccess) {
    return (
      <>
        <Table.TitleWrapper name='TableTitleWrapper'>{toolbar}</Table.TitleWrapper>
        <Table.Content>
          <Table.Header
            columns={visibleColumns}
            order={order}
            sortList={sortList}
            lineActions={lineActions}
            listActions={listActions}
            selectAllState={selection.selectAllState}
            onSelectAll={selection.selectAll}
            selectedCount={selection.selectedCount}
            onListAction={(action) => onBulkListAction(action)}
            selectable={!!selectAction}
          />
          {result && (
            <ViewTableRows
              endpoint={endpoint}
              result={result}
              visibleColumns={visibleColumns}
              currentFilter={currentFilter}
              filterIndex={filter}
              table={table}
              debouncedSearchTerm={debouncedSearchTerm}
              lineActions={lineActions}
              listActions={listActions}
              selection={selection}
              handleAction={handleAction}
              handleExpand={handleExpand}
              selectAction={selectAction}
            />
          )}
          {result && (
            <ViewTableRows_m
              endpoint={endpoint}
              result={result}
              currentFilter={currentFilter}
              filterIndex={filter}
              table={table}
              debouncedSearchTerm={debouncedSearchTerm}
              lineActions={lineActions}
              listActions={listActions}
              handleAction={handleAction}
              handleExpand={handleExpand}
              order={order}
              sortList={sortList}
              selectAllState={selection.selectAllState}
              onSelectAll={selection.selectAll}
              selectedCount={selection.selectedCount}
              onListAction={(action) => onBulkListAction(action)}
              selectable={!!selectAction}
              selectedItems={selection.selectedItems}
              onItemSelect={(itemId, opts) => selection.toggleItem(itemId, opts)}
            />
          )}
          <SearchLimitNote result={result} />
        </Table.Content>
        {result.hasNextPage && !result.hasMore && (
          <Table.Foot>
            <LoadMore
              $variant='transparent'
              onClick={() => result.fetchNextPage()}
              disabled={result.isFetchingNextPage}
            >
              {result.isFetchingNextPage ? 'Loading...' : 'Load More'}
            </LoadMore>
          </Table.Foot>
        )}
        {moveItem && (
          <MoveFolderModal
            item={moveItem}
            onClose={clearMoveItem}
            onConfirm={confirmMove}
            isLoading={moveLoading}
          />
        )}
      </>
    )
  }

  return null
}

export default ViewTable
